import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { dailyItemService, businessUserService } from "../services/authService";
import moment from "moment";

const DeliveryTracking = ({ user }) => {
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [users, setUsers] = useState([]); // For user filter dropdown
  const [selectedUser, setSelectedUser] = useState(""); // User filter
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [showConcernModal, setShowConcernModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [concernMessage, setConcernMessage] = useState("");
  const [filter, setFilter] = useState("all"); // all, pending, delivered, missed
  const [categoryFilter, setCategoryFilter] = useState("all"); // Category filter for regular users
  const [modifyingLogId, setModifyingLogId] = useState(null); // Track which log is being modified
  const [showConcernsViewModal, setShowConcernsViewModal] = useState(false); // View all concerns modal
  const [selectedLogForConcerns, setSelectedLogForConcerns] = useState(null); // Log to view concerns for
  const [customResponseEnabled, setCustomResponseEnabled] = useState(false); // Toggle for custom admin response
  const [customResponseMessage, setCustomResponseMessage] = useState(""); // Custom admin response text
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false); // Delete confirmation modal
  const [concernToDelete, setConcernToDelete] = useState(null); // Concern to be deleted
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  // Quantity change states
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showQuantityRequestsModal, setShowQuantityRequestsModal] =
    useState(false); // Separate modal for viewing quantity requests
  const [selectedLogForQuantity, setSelectedLogForQuantity] = useState(null);
  const [newQuantity, setNewQuantity] = useState("");
  const [quantityChangeReason, setQuantityChangeReason] = useState("");
  const [isQuantityRequest, setIsQuantityRequest] = useState(false); // true for user requests, false for admin changes

  const fetchDeliveryLogs = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        }
        const startDate = moment(selectedDate).startOf("month").toISOString();
        const endDate = moment(selectedDate).endOf("month").toISOString();

        const logsData = await dailyItemService.getDeliveryLogs(
          startDate,
          endDate,
          selectedUser || null // Pass user filter if selected
        );

        const previousCount = deliveryLogs.length;
        const newCount = logsData.length;

        // Notify if logs were removed (only during silent updates)
        if (silent && previousCount > 0 && newCount < previousCount) {
          const removedCount = previousCount - newCount;
          toast.info(
            `${removedCount} delivery log${removedCount > 1 ? "s" : ""} ${
              removedCount > 1 ? "have" : "has"
            } been removed due to daily item deletion.`,
            {
              position: "top-right",
              autoClose: 4000,
            }
          );
        }

        setDeliveryLogs(logsData);
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Error fetching delivery logs:", error);
        if (!silent) {
          toast.error("Failed to fetch delivery logs");
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [selectedDate, selectedUser, deliveryLogs.length]
  );

  const fetchUsers = useCallback(async () => {
    if (user.role !== "admin") return; // Only fetch users for admin

    try {
      const usersData = await businessUserService.getAllUsers();
      setUsers(usersData); // Business users (already filtered by business relationship)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [user.role]);

  useEffect(() => {
    fetchDeliveryLogs();
    fetchUsers();

    // Set up polling to check for updates every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchDeliveryLogs(true); // Silent refresh
    }, 30000);

    // Add window focus listener for manual refresh when user comes back
    const handleFocus = () => {
      fetchDeliveryLogs(true);
    };

    window.addEventListener("focus", handleFocus);

    // Cleanup interval and listener on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchDeliveryLogs, fetchUsers]);

  const handleStatusUpdate = async (logId, status, notes = "") => {
    if (user.role !== "admin") {
      toast.error("Only business users can update delivery status");
      return;
    }

    try {
      await dailyItemService.updateDeliveryStatus(logId, status, notes);

      // If this update is addressing concerns, automatically resolve all open concerns
      const log = deliveryLogs.find((l) => l._id === logId);
      if (log && log.concerns) {
        const openConcerns = log.concerns.filter((c) => c.status === "open");

        // Resolve all open concerns
        for (const concern of openConcerns) {
          try {
            await dailyItemService.respondToConcern(
              logId,
              concern._id,
              `Concern addressed - delivery status updated to "${status}"${
                notes ? ` - ${notes}` : ""
              }`,
              "resolved"
            );
          } catch (concernError) {
            console.error("Error resolving concern:", concernError);
            // Continue with other concerns even if one fails
          }
        }
      }

      // Provide context-aware success messages
      if (notes.includes("concerns")) {
        toast.success(
          `Delivery status updated to "${status}" - addressing customer concerns`
        );
      } else {
        toast.success("Delivery status updated successfully");
      }

      fetchDeliveryLogs();
    } catch (error) {
      console.error("Error updating delivery status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update delivery status"
      );
    }
  };

  // New function for handling status updates with custom responses from concerns modal
  const handleStatusUpdateWithCustomResponse = async (logId, status) => {
    if (user.role !== "admin") {
      toast.error("Only business users can update delivery status");
      return;
    }

    try {
      await dailyItemService.updateDeliveryStatus(
        logId,
        status,
        "Status updated from concerns review"
      );

      // If this update is addressing concerns, automatically resolve all open concerns
      const log = deliveryLogs.find((l) => l._id === logId);
      if (log && log.concerns) {
        const openConcerns = log.concerns.filter((c) => c.status === "open");

        // Determine the admin response message
        let adminResponse;
        if (customResponseEnabled && customResponseMessage.trim()) {
          // Use custom message with status
          adminResponse = `Admin ${status} delivery - ${customResponseMessage.trim()}`;
        } else {
          // Use default message
          adminResponse = `Concern addressed - delivery status updated to "${status}"`;
        }

        // Resolve all open concerns with the appropriate response
        for (const concern of openConcerns) {
          try {
            await dailyItemService.respondToConcern(
              logId,
              concern._id,
              adminResponse,
              "resolved"
            );
          } catch (concernError) {
            console.error("Error resolving concern:", concernError);
            // Continue with other concerns even if one fails
          }
        }
      }

      toast.success(
        `Delivery status updated to "${status}" with custom response`
      );

      // Reset custom response state
      setCustomResponseEnabled(false);
      setCustomResponseMessage("");

      // Close modal and refresh data
      setShowConcernsViewModal(false);
      setSelectedLogForConcerns(null);
      fetchDeliveryLogs();
    } catch (error) {
      console.error("Error updating delivery status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update delivery status"
      );
    }
  };

  const handleRaiseConcern = async () => {
    if (!concernMessage.trim()) {
      toast.error("Please enter a concern message");
      return;
    }

    try {
      await dailyItemService.raiseConcern(selectedLog._id, concernMessage);
      toast.success("Concern raised successfully");
      setConcernMessage("");
      setShowConcernModal(false);
      setSelectedLog(null);
      fetchDeliveryLogs();
    } catch (error) {
      console.error("Error raising concern:", error);
      toast.error(error.response?.data?.message || "Failed to raise concern");
    }
  };
  const handleDeleteConcern = (logId, concernId, concernRaisedBy) => {
    // Check if user can delete this concern
    if (user.role !== "admin" && concernRaisedBy?._id !== user.id) {
      toast.error("You can only delete concerns you raised");
      return;
    }

    // Set concern to delete and show confirmation modal
    setConcernToDelete({ logId, concernId, concernRaisedBy });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteConcern = async () => {
    if (!concernToDelete) return;

    try {
      await dailyItemService.deleteConcern(
        concernToDelete.logId,
        concernToDelete.concernId
      );
      toast.success("Concern deleted successfully");

      // Refresh the delivery logs data
      await fetchDeliveryLogs();

      // If viewing concerns modal, update the selected log data immediately
      if (showConcernsViewModal && selectedLogForConcerns) {
        // Update the selectedLogForConcerns by removing the deleted concern
        const updatedConcerns = selectedLogForConcerns.concerns.filter(
          (concern) => concern._id !== concernToDelete.concernId
        );
        setSelectedLogForConcerns({
          ...selectedLogForConcerns,
          concerns: updatedConcerns,
        });
      }

      // Close confirmation modal and reset state
      setShowDeleteConfirmModal(false);
      setConcernToDelete(null);
    } catch (error) {
      console.error("Error deleting concern:", error);
      toast.error(error.response?.data?.message || "Failed to delete concern");
    }
  };

  const cancelDeleteConcern = () => {
    setShowDeleteConfirmModal(false);
    setConcernToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "#28a745";
      case "missed":
        return "#dc3545";
      case "cancelled":
        return "#6c757d";
      case "pending":
        return "#ffc107";
      default:
        return "#6c757d";
    }
  };

  // Quantity change functions
  const handleQuantityChange = (log, isRequest = false) => {
    setSelectedLogForQuantity(log);
    const currentQuantity = log.quantityChanged
      ? log.newQuantity
      : log.dailyItem?.scheduleQuantity;
    setNewQuantity(currentQuantity ? currentQuantity.toString() : "");
    setQuantityChangeReason("");
    setIsQuantityRequest(isRequest);
    setShowQuantityModal(true);
  };

  const handleQuantitySubmit = async () => {
    if (!selectedLogForQuantity || !newQuantity || !newQuantity.trim()) {
      toast.error("Please enter a valid quantity");
      return;
    }

    const quantityNum = parseInt(newQuantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      toast.error("Quantity must be a positive number");
      return;
    }

    if (isQuantityRequest && !quantityChangeReason.trim()) {
      toast.error("Please provide a reason for your quantity change request");
      return;
    }

    try {
      if (isQuantityRequest) {
        // User requesting quantity change
        const message = `Quantity change request for ${formatDate(
          selectedLogForQuantity.deliveryDate
        )}: 
        
Current quantity: ${
          selectedLogForQuantity.quantityChanged
            ? selectedLogForQuantity.newQuantity
            : selectedLogForQuantity.dailyItem?.quantity || "Not specified"
        }
Requested quantity: ${newQuantity}
${quantityChangeReason ? `Reason: ${quantityChangeReason}` : ""}`;

        await dailyItemService.raiseConcern(
          selectedLogForQuantity._id,
          message
        );
        toast.success("Quantity change request sent to admin");
      } else {
        // Admin making direct quantity change
        await dailyItemService.updateDeliveryQuantity(
          selectedLogForQuantity._id,
          newQuantity,
          quantityChangeReason
        );
        toast.success("Delivery quantity updated successfully");
      }

      setShowQuantityModal(false);
      setSelectedLogForQuantity(null);
      setNewQuantity("");
      setQuantityChangeReason("");
      fetchDeliveryLogs();
    } catch (error) {
      console.error("Error handling quantity change:", error);
      toast.error(
        error.response?.data?.message || "Failed to process quantity change"
      );
    }
  };

  // Function to handle viewing quantity requests separately
  const handleViewQuantityRequests = (log) => {
    setSelectedLogForConcerns(log);
    setShowQuantityRequestsModal(true);
  };

  // Function to approve a quantity change request
  const handleApproveQuantityRequest = async (concern, requestedQuantity) => {
    try {
      // Extract the requested quantity from the concern message
      const quantityMatch = concern.message.match(
        /Requested quantity:\s*(\d+)/
      );
      const quantity = quantityMatch ? quantityMatch[1] : requestedQuantity;

      if (!quantity) {
        toast.error("Could not determine requested quantity");
        return;
      }

      // Update the delivery quantity
      await dailyItemService.updateDeliveryQuantity(
        selectedLogForConcerns._id,
        quantity,
        `Approved user request: ${
          concern.message.split("Reason:")[1]?.trim() ||
          "User quantity change request"
        }`
      );

      // Respond to the concern as resolved
      await dailyItemService.respondToConcern(
        selectedLogForConcerns._id,
        concern._id,
        `Quantity change approved and updated to ${quantity}`,
        "resolved"
      );

      toast.success(`Quantity change approved and updated to ${quantity}`);
      setShowQuantityRequestsModal(false);
      fetchDeliveryLogs();
    } catch (error) {
      console.error("Error approving quantity request:", error);
      toast.error("Failed to approve quantity request");
    }
  };

  const getFilteredLogs = () => {
    let logs = deliveryLogs;

    // Apply status filter
    if (filter === "with-concerns") {
      logs = logs.filter((log) => log.concerns && log.concerns.length > 0);
    } else if (filter === "open-concerns") {
      logs = logs.filter(
        (log) =>
          log.concerns &&
          log.concerns.some((concern) => concern.status === "open")
      );
    } else if (filter !== "all") {
      logs = logs.filter((log) => log.status === filter);
    }

    // Apply category filter (for all users now, including admins)
    if (categoryFilter !== "all") {
      logs = logs.filter((log) => log.dailyItem?.category === categoryFilter);
    }

    return logs;
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY");
  };

  const formatTime = (dateString) => {
    return moment(dateString).format("HH:mm");
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const filteredLogs = getFilteredLogs();

  // Monthly summary statistics for the selected month (using filtered logs)
  const monthlyStats = {
    delivered: filteredLogs.filter((log) => log.status === "delivered").length,
    pending: filteredLogs.filter((log) => log.status === "pending").length,
    missed: filteredLogs.filter((log) => log.status === "missed").length,
    cancelled: filteredLogs.filter((log) => log.status === "cancelled").length,
  };

  const selectedMonth = moment(selectedDate).format("MMMM YYYY");

  return (
    <div className="card">
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <h2>🚚 Delivery Tracking</h2>
            <p style={{ margin: 0 }}>
              {user.role === "admin"
                ? "Monitor all deliveries"
                : "Track your deliveries"}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            {lastUpdated && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                }}
              >
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => fetchDeliveryLogs()}
              disabled={loading}
              style={{
                padding: "8px 15px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {loading ? "🔄" : "🔄"} Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Today's Summary */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            padding: "15px",
          }}
        >
          <h4>� Monthly Summary ({selectedMonth})</h4>
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginTop: "10px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong style={{ color: "#28a745" }}>
                Delivered: {monthlyStats.delivered}
              </strong>
            </div>
            <div>
              <strong style={{ color: "#ffc107" }}>
                Pending: {monthlyStats.pending}
              </strong>
            </div>
            <div>
              <strong style={{ color: "#dc3545" }}>
                Missed: {monthlyStats.missed}
              </strong>
            </div>
            <div>
              <strong style={{ color: "#6c757d" }}>
                Cancelled: {monthlyStats.cancelled}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "nowrap",
          overflowX: "auto",
          padding: "5px",
        }}
      >
        <div>
          <label style={{ marginRight: "8px", fontWeight: "bold" }}>
            Month:
          </label>
          <input
            type="month"
            value={moment(selectedDate).format("YYYY-MM")}
            onChange={(e) => setSelectedDate(e.target.value + "-01")}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "120px",
            }}
          />
        </div>
        <div>
          <label style={{ marginRight: "8px", fontWeight: "bold" }}>
            Status:
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "130px",
            }}
          >
            <option value="all">All Deliveries</option>
            <option value="pending">Pending</option>
            <option value="delivered">Delivered</option>
            <option value="missed">Missed</option>
            <option value="cancelled">Cancelled</option>
            {user.role === "admin" && (
              <>
                <option value="with-concerns">🚨 With Concerns</option>
                <option value="open-concerns">🔴 Open Concerns</option>
              </>
            )}
          </select>
        </div>
        {/* Category Filter (Regular users only) */}
        {user.role !== "admin" && (
          <div>
            <label style={{ marginRight: "8px", fontWeight: "bold" }}>
              Category:
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
                minWidth: "140px",
              }}
            >
              <option value="all">All Categories</option>
              <option value="milk">Milk</option>
              <option value="newspaper">Newspaper</option>
              <option value="groceries">Groceries</option>
              <option value="medicine">Medicine</option>
              <option value="other">Other</option>
            </select>
          </div>
        )}{" "}
        {/* User Filter (Admin only) */}
        {user.role === "admin" && (
          <>
            <div>
              <label style={{ marginRight: "8px", fontWeight: "bold" }}>
                User:
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  width: "200px",
                }}
              >
                <option value="">All Users</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.firstName} {u.lastName} ({u.uniqueUserId})
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter for Admin */}
            <div style={{ marginLeft: "10px" }}>
              <label style={{ marginRight: "8px", fontWeight: "bold" }}>
                Category:
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  width: "110px",
                }}
              >
                <option value="all">All Categories</option>
                <option value="milk">Milk</option>
                <option value="newspaper">Newspaper</option>
                <option value="groceries">Groceries</option>
                <option value="medicine">Medicine</option>
                <option value="other">Other</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Delivery Logs */}
      {filteredLogs.length === 0 ? (
        <div className="text-center" style={{ padding: "40px" }}>
          <p>No delivery logs found for the selected criteria.</p>
        </div>
      ) : (
        <div
          style={{
            maxHeight: "600px",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <table className="table" style={{ marginBottom: "0" }}>
            <thead
              style={{
                position: "sticky",
                top: "0",
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
                zIndex: 10,
              }}
            >
              <tr>
                <th>Date</th>
                <th>Item</th>
                {user.role === "admin" && <th>Customer</th>}
                <th>Status</th>
                <th>Delivery Time</th>
                <th style={{ width: "300px", minWidth: "250px" }}>
                  Concerns & Comments
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log._id}>
                  <td>{formatDate(log.deliveryDate)}</td>
                  <td>
                    <div>
                      <strong>{log.dailyItem?.title}</strong>
                      {(log.dailyItem?.scheduleQuantity ||
                        log.quantityChanged) && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          Qty:{" "}
                          {log.quantityChanged
                            ? log.newQuantity
                            : log.dailyItem.scheduleQuantity}
                          {log.quantityChanged && (
                            <span
                              style={{
                                color: "#28a745",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              (Changed from {log.originalQuantity})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  {user.role === "admin" && (
                    <td>
                      <div>
                        <strong>
                          {log.assignedTo?.firstName} {log.assignedTo?.lastName}
                        </strong>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          ID: {log.assignedTo?.uniqueUserId}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {log.assignedTo?.email}
                        </div>
                      </div>
                    </td>
                  )}
                  <td>
                    <span
                      style={{
                        color: getStatusColor(log.status),
                        fontWeight: "bold",
                        textTransform: "capitalize",
                      }}
                    >
                      {log.status}
                    </span>
                    {log.deliveredAt && (
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        at {formatTime(log.deliveredAt)}
                      </div>
                    )}
                  </td>
                  <td>{log.dailyItem?.deliveryTime || "N/A"}</td>
                  <td>
                    {log.concerns && log.concerns.length > 0 ? (
                      <div>
                        <button
                          className="btn btn-link"
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            textDecoration: "none",
                            color: "#dc3545",
                            fontWeight: "bold",
                            border: "1px solid #dc3545",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                          }}
                          onClick={() => {
                            setSelectedLogForConcerns(log);
                            setShowConcernsViewModal(true);
                          }}
                        >
                          📋 {log.concerns.length} concern
                          {log.concerns.length !== 1 ? "s" : ""}
                          {log.concerns.filter((c) => c.status === "open")
                            .length > 0 && (
                            <span
                              style={{ marginLeft: "5px", fontSize: "10px" }}
                            >
                              (
                              {
                                log.concerns.filter((c) => c.status === "open")
                                  .length
                              }{" "}
                              open)
                            </span>
                          )}
                          {log.concerns.some((concern) =>
                            concern.message
                              .toLowerCase()
                              .includes("quantity change request")
                          ) && (
                            <span
                              style={{
                                marginLeft: "5px",
                                fontSize: "10px",
                                background: "#ffc107",
                                padding: "2px 4px",
                                borderRadius: "3px",
                                color: "#212529",
                              }}
                            >
                              📦 QTY
                            </span>
                          )}
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: "#28a745" }}>No concerns</span>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      {/* Priority indicator for admins when there are concerns */}
                      {user.role === "admin" &&
                        log.concerns &&
                        log.concerns.some((c) => c.status === "open") &&
                        (() => {
                          // Check if admin has updated status after the most recent concern
                          const mostRecentConcern = log.concerns
                            .filter((c) => c.status === "open")
                            .sort(
                              (a, b) =>
                                new Date(b.raisedAt) - new Date(a.raisedAt)
                            )[0];

                          if (!mostRecentConcern) return false;

                          // If delivery was updated after the most recent concern, don't show attention indicator
                          const lastUpdate = log.updatedAt || log.createdAt;
                          const concernRaisedAt = mostRecentConcern.raisedAt;

                          return (
                            new Date(concernRaisedAt) > new Date(lastUpdate)
                          );
                        })() && (
                          <div
                            style={{
                              backgroundColor: "#fff3cd",
                              border: "1px solid #ffeaa7",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              marginBottom: "8px",
                              fontSize: "10px",
                              fontWeight: "bold",
                              color: "#856404",
                            }}
                          >
                            🚨 NEEDS ATTENTION
                          </div>
                        )}

                      {user.role === "admin" && (
                        <>
                          {log.status === "pending" && (
                            <>
                              <button
                                className="btn btn-success"
                                style={{ padding: "4px 8px", fontSize: "11px" }}
                                onClick={() =>
                                  handleStatusUpdate(log._id, "delivered")
                                }
                              >
                                Mark Delivered
                              </button>
                              <button
                                className="btn btn-warning"
                                style={{ padding: "4px 8px", fontSize: "11px" }}
                                onClick={() =>
                                  handleStatusUpdate(log._id, "missed")
                                }
                              >
                                Mark Missed
                              </button>
                              <button
                                className="btn btn-danger"
                                style={{ padding: "4px 8px", fontSize: "11px" }}
                                onClick={() =>
                                  handleStatusUpdate(log._id, "cancelled")
                                }
                              >
                                Cancel
                              </button>
                            </>
                          )}

                          {/* Quantity Change Button for Admin */}
                          <button
                            className="btn btn-info"
                            style={{
                              padding: "4px 8px",
                              fontSize: "11px",
                              marginTop: "4px",
                              background:
                                "linear-gradient(135deg, #17a2b8, #138496)",
                              border: "none",
                              color: "white",
                            }}
                            onClick={() => handleQuantityChange(log, false)}
                          >
                            📦 Change Quantity
                          </button>

                          {/* View Quantity Requests Button */}
                          {log.concerns &&
                            log.concerns.some((concern) =>
                              concern.message
                                .toLowerCase()
                                .includes("quantity change request")
                            ) && (
                              <button
                                className="btn btn-warning"
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                  marginTop: "4px",
                                  marginLeft: "4px",
                                  background:
                                    "linear-gradient(135deg, #ffc107, #e0a800)",
                                  border: "none",
                                  color: "#212529",
                                }}
                                onClick={() => handleViewQuantityRequests(log)}
                              >
                                📋 View Qty Requests
                              </button>
                            )}

                          {/* Modify options for non-pending deliveries */}
                          {log.status !== "pending" &&
                            modifyingLogId === log._id && (
                              <div style={{ marginBottom: "8px" }}>
                                <div
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                    color: "#495057",
                                  }}
                                >
                                  Change status to:
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "3px",
                                  }}
                                >
                                  {log.status !== "delivered" && (
                                    <button
                                      className="btn btn-success"
                                      style={{
                                        padding: "3px 6px",
                                        fontSize: "10px",
                                      }}
                                      onClick={() => {
                                        handleStatusUpdate(
                                          log._id,
                                          "delivered"
                                        );
                                        setModifyingLogId(null);
                                      }}
                                    >
                                      ✅ Delivered
                                    </button>
                                  )}
                                  {log.status !== "missed" && (
                                    <button
                                      className="btn btn-warning"
                                      style={{
                                        padding: "3px 6px",
                                        fontSize: "10px",
                                      }}
                                      onClick={() => {
                                        handleStatusUpdate(log._id, "missed");
                                        setModifyingLogId(null);
                                      }}
                                    >
                                      ❌ Missed
                                    </button>
                                  )}
                                  {log.status !== "cancelled" && (
                                    <button
                                      className="btn btn-danger"
                                      style={{
                                        padding: "3px 6px",
                                        fontSize: "10px",
                                      }}
                                      onClick={() => {
                                        handleStatusUpdate(
                                          log._id,
                                          "cancelled"
                                        );
                                        setModifyingLogId(null);
                                      }}
                                    >
                                      🚫 Cancelled
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-secondary"
                                    style={{
                                      padding: "3px 6px",
                                      fontSize: "10px",
                                    }}
                                    onClick={() => {
                                      handleStatusUpdate(log._id, "pending");
                                      setModifyingLogId(null);
                                    }}
                                  >
                                    🔄 Reset to Pending
                                  </button>
                                  <button
                                    className="btn btn-light"
                                    style={{
                                      padding: "3px 6px",
                                      fontSize: "10px",
                                      border: "1px solid #dee2e6",
                                    }}
                                    onClick={() => setModifyingLogId(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                          {/* Modify button for non-pending deliveries */}
                          {log.status !== "pending" &&
                            modifyingLogId !== log._id && (
                              <button
                                className="btn btn-info"
                                style={{ padding: "4px 8px", fontSize: "11px" }}
                                onClick={() => setModifyingLogId(log._id)}
                              >
                                🔧 Modify Status
                              </button>
                            )}
                        </>
                      )}

                      {(user.role !== "admin" ||
                        log.assignedTo?._id === user.id) && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "4px 8px", fontSize: "11px" }}
                          onClick={() => {
                            setSelectedLog(log);
                            setShowConcernModal(true);
                          }}
                        >
                          Raise Concern
                        </button>
                      )}

                      {/* Quantity Request Button for Users */}
                      {(user.role !== "admin" ||
                        log.assignedTo?._id === user.id) && (
                        <button
                          className="btn btn-warning"
                          style={{
                            padding: "4px 8px",
                            fontSize: "11px",
                            marginTop: "4px",
                            background:
                              "linear-gradient(135deg, #ffc107, #e0a800)",
                            border: "none",
                            color: "#212529",
                          }}
                          onClick={() => handleQuantityChange(log, true)}
                        >
                          📦 Request Quantity Change
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Summary */}
      {filteredLogs.length > 0 && (
        <div
          style={{
            marginTop: "10px",
            padding: "8px 12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#6c757d",
            textAlign: "center",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            Showing {filteredLogs.length} delivery log
            {filteredLogs.length !== 1 ? "s" : ""}
          </span>
          {filteredLogs.length > 10 && (
            <span style={{ fontSize: "12px" }}>
              📜 Scroll within the table above to see all entries
            </span>
          )}
        </div>
      )}

      {/* Concern Modal */}
      {showConcernModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "20px 20px 0 0",
                color: "white",
                padding: "25px 30px",
                borderBottom: "none",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
                🚨 Raise Delivery Concern
              </h3>
              <button
                className="btn-close"
                onClick={() => setShowConcernModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: "30px" }}>
              <div style={{ marginBottom: "15px" }}>
                <strong>Item:</strong> {selectedLog?.dailyItem?.title}
                <br />
                <strong>Date:</strong>{" "}
                {selectedLog && formatDate(selectedLog.deliveryDate)}
                <br />
                <strong>Status:</strong>{" "}
                <span style={{ color: getStatusColor(selectedLog?.status) }}>
                  {selectedLog?.status}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="concernMessage">Describe your concern:</label>
                <textarea
                  id="concernMessage"
                  value={concernMessage}
                  onChange={(e) => setConcernMessage(e.target.value)}
                  rows="4"
                  placeholder="Please describe the issue with this delivery..."
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "20px 30px 30px",
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "0 0 20px 20px",
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowConcernModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRaiseConcern}>
                Submit Concern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Concerns View Modal */}
      {showConcernsViewModal && selectedLogForConcerns && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: "700px",
              width: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "20px 20px 0 0",
                color: "white",
                padding: "25px 30px",
                borderBottom: "none",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
                📋 Delivery Concerns
              </h3>
              <button
                className="btn-close"
                onClick={() => {
                  setShowConcernsViewModal(false);
                  setSelectedLogForConcerns(null);
                  setCustomResponseEnabled(false);
                  setCustomResponseMessage("");
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: "30px" }}>
              {/* Delivery Info */}
              <div
                style={{
                  marginBottom: "20px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              >
                <h4 style={{ marginBottom: "10px", color: "#495057" }}>
                  🎯 {selectedLogForConcerns.dailyItem?.title}
                </h4>
                <div style={{ display: "flex", gap: "20px", fontSize: "14px" }}>
                  <div>
                    <strong>Date:</strong>{" "}
                    {formatDate(selectedLogForConcerns.deliveryDate)}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    <span
                      style={{
                        color: getStatusColor(selectedLogForConcerns.status),
                      }}
                    >
                      {selectedLogForConcerns.status}
                    </span>
                  </div>
                  <div>
                    <strong>Time:</strong>{" "}
                    {selectedLogForConcerns.dailyItem?.deliveryTime || "N/A"}
                  </div>
                </div>
              </div>

              {/* Concerns List */}
              <div>
                <h4 style={{ marginBottom: "15px", color: "#495057" }}>
                  🚨 Concerns ({selectedLogForConcerns.concerns?.length || 0})
                </h4>

                {selectedLogForConcerns.concerns &&
                selectedLogForConcerns.concerns.length > 0 ? (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {selectedLogForConcerns.concerns
                      .sort(
                        (a, b) => new Date(b.raisedAt) - new Date(a.raisedAt)
                      )
                      .map((concern, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "15px",
                            padding: "15px",
                            backgroundColor:
                              concern.status === "open" ? "#fff3cd" : "#d1ecf1",
                            borderRadius: "8px",
                            border:
                              "1px solid " +
                              (concern.status === "open"
                                ? "#ffeaa7"
                                : "#bee5eb"),
                          }}
                        >
                          {/* Concern Header */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <div
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              👤 {concern.raisedBy?.username || "User"}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <div
                                style={{ fontSize: "12px", color: "#6c757d" }}
                              >
                                {new Date(concern.raisedAt).toLocaleString()}
                              </div>
                              {/* Delete button - only show for concern owner or admin, and only for open concerns */}
                              {(user.role === "admin" ||
                                concern.raisedBy?._id === user.id) &&
                                concern.status === "open" && (
                                  <button
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #ff6b6b, #ee5a5a)",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "4px 8px",
                                      fontSize: "11px",
                                      color: "white",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      transition: "all 0.2s ease",
                                      boxShadow:
                                        "0 2px 4px rgba(255, 107, 107, 0.3)",
                                      minWidth: "auto",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform =
                                        "translateY(-1px)";
                                      e.target.style.boxShadow =
                                        "0 4px 8px rgba(255, 107, 107, 0.4)";
                                      e.target.style.background =
                                        "linear-gradient(135deg, #ff5252, #f44336)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform =
                                        "translateY(0)";
                                      e.target.style.boxShadow =
                                        "0 2px 4px rgba(255, 107, 107, 0.3)";
                                      e.target.style.background =
                                        "linear-gradient(135deg, #ff6b6b, #ee5a5a)";
                                    }}
                                    onClick={() =>
                                      handleDeleteConcern(
                                        selectedLogForConcerns._id,
                                        concern._id,
                                        concern.raisedBy
                                      )
                                    }
                                    title={
                                      user.role === "admin"
                                        ? "Delete this concern"
                                        : "Delete your concern"
                                    }
                                  >
                                    <span style={{ fontSize: "10px" }}>🗑️</span>
                                    <span style={{ fontWeight: "500" }}>
                                      Delete
                                    </span>
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* Status Badge */}
                          <div style={{ marginBottom: "10px" }}>
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "bold",
                                backgroundColor:
                                  concern.status === "open"
                                    ? "#dc3545"
                                    : "#28a745",
                                color: "white",
                              }}
                            >
                              {concern.status === "open"
                                ? "🔴 OPEN"
                                : "✅ RESOLVED"}
                            </span>
                          </div>

                          {/* Concern Message */}
                          <div
                            style={{
                              padding: "10px",
                              backgroundColor: concern.message
                                .toLowerCase()
                                .includes("quantity change request")
                                ? "#fff3e0"
                                : "white",
                              border: concern.message
                                .toLowerCase()
                                .includes("quantity change request")
                                ? "2px solid #ff9800"
                                : "1px solid #e0e0e0",
                              borderRadius: "6px",
                              marginBottom: "10px",
                              fontSize: "14px",
                              lineHeight: "1.4",
                            }}
                          >
                            {concern.message
                              .toLowerCase()
                              .includes("quantity change request") && (
                              <div
                                style={{
                                  color: "#e65100",
                                  fontWeight: "bold",
                                  marginBottom: "8px",
                                  fontSize: "12px",
                                }}
                              >
                                📦 QUANTITY CHANGE REQUEST
                              </div>
                            )}
                            {concern.message}
                          </div>

                          {/* Admin Response */}
                          {concern.adminResponse && (
                            <div
                              style={{
                                padding: "10px",
                                backgroundColor: "#e8f5e8",
                                borderRadius: "6px",
                                borderLeft: "4px solid #28a745",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  color: "#28a745",
                                  marginBottom: "5px",
                                }}
                              >
                                👨‍💼 Admin Response:
                              </div>
                              <div
                                style={{ fontSize: "14px", color: "#495057" }}
                              >
                                {concern.adminResponse}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#6c757d",
                      fontSize: "16px",
                    }}
                  >
                    ✅ No concerns raised for this delivery
                  </div>
                )}
              </div>

              {/* Admin Quick Actions */}
              {user.role === "admin" &&
                selectedLogForConcerns.concerns &&
                selectedLogForConcerns.concerns.some(
                  (c) => c.status === "open"
                ) && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "15px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    <h5 style={{ marginBottom: "15px", color: "#495057" }}>
                      🔧 Quick Business Actions
                    </h5>

                    {/* Custom Response Toggle */}
                    <div style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={customResponseEnabled}
                          onChange={(e) => {
                            setCustomResponseEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setCustomResponseMessage("");
                            }
                          }}
                          style={{ transform: "scale(1.2)" }}
                        />
                        <span style={{ fontWeight: "bold", color: "#495057" }}>
                          💬 Add custom response to concerns
                        </span>
                      </label>

                      {customResponseEnabled && (
                        <div style={{ marginTop: "10px" }}>
                          <textarea
                            value={customResponseMessage}
                            onChange={(e) =>
                              setCustomResponseMessage(e.target.value)
                            }
                            placeholder="Enter your custom response to address the customer concerns..."
                            rows="3"
                            style={{
                              width: "100%",
                              padding: "8px",
                              border: "1px solid #dee2e6",
                              borderRadius: "4px",
                              fontSize: "14px",
                              resize: "vertical",
                            }}
                          />
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6c757d",
                              marginTop: "5px",
                            }}
                          >
                            This message will be shown as: "Admin [status]
                            delivery - [your message]"
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div
                      style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                    >
                      {selectedLogForConcerns.status !== "delivered" && (
                        <button
                          className="btn btn-success"
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                          onClick={() =>
                            handleStatusUpdateWithCustomResponse(
                              selectedLogForConcerns._id,
                              "delivered"
                            )
                          }
                        >
                          ✅ Mark Delivered
                        </button>
                      )}
                      {selectedLogForConcerns.status !== "missed" && (
                        <button
                          className="btn btn-warning"
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                          onClick={() =>
                            handleStatusUpdateWithCustomResponse(
                              selectedLogForConcerns._id,
                              "missed"
                            )
                          }
                        >
                          ❌ Mark Missed
                        </button>
                      )}
                      {selectedLogForConcerns.status !== "cancelled" && (
                        <button
                          className="btn btn-danger"
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                          onClick={() =>
                            handleStatusUpdateWithCustomResponse(
                              selectedLogForConcerns._id,
                              "cancelled"
                            )
                          }
                        >
                          🚫 Cancel
                        </button>
                      )}
                      {selectedLogForConcerns.status !== "pending" && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                          onClick={() =>
                            handleStatusUpdateWithCustomResponse(
                              selectedLogForConcerns._id,
                              "pending"
                            )
                          }
                        >
                          🔄 Reset to Pending
                        </button>
                      )}
                    </div>
                  </div>
                )}
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "20px 30px 30px",
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "0 0 20px 20px",
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowConcernsViewModal(false);
                  setSelectedLogForConcerns(null);
                  setCustomResponseEnabled(false);
                  setCustomResponseMessage("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Concern Confirmation Modal */}
      {showDeleteConfirmModal && concernToDelete && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: "400px",
              width: "90%",
              maxHeight: "70vh",
              overflowY: "auto",
              background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "20px 20px 0 0",
                color: "white",
                padding: "25px 30px",
                borderBottom: "none",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
                🗑️ Delete Concern
              </h3>
              <button
                className="btn-close"
                onClick={cancelDeleteConcern}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: "30px" }}>
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div
                  style={{
                    fontSize: "48px",
                    marginBottom: "15px",
                    filter: "grayscale(20%)",
                  }}
                >
                  ⚠️
                </div>
                <h4
                  style={{
                    marginBottom: "15px",
                    color: "#495057",
                    fontWeight: "600",
                  }}
                >
                  Are you sure?
                </h4>
                <p
                  style={{
                    color: "#6c757d",
                    lineHeight: "1.5",
                    marginBottom: "0",
                  }}
                >
                  This concern will be permanently deleted and cannot be
                  recovered.
                  {user.role === "admin"
                    ? " You are deleting another user's concern."
                    : " You are deleting your own concern."}
                </p>
              </div>
            </div>

            <div
              className="modal-footer"
              style={{ justifyContent: "center", gap: "15px" }}
            >
              <button
                className="btn btn-secondary"
                onClick={cancelDeleteConcern}
                style={{
                  padding: "8px 20px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  background: "linear-gradient(135deg, #ff6b6b, #ee5a5a)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 20px",
                  fontSize: "14px",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(255, 107, 107, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow =
                    "0 4px 8px rgba(255, 107, 107, 0.4)";
                  e.target.style.background =
                    "linear-gradient(135deg, #ff5252, #f44336)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 2px 4px rgba(255, 107, 107, 0.3)";
                  e.target.style.background =
                    "linear-gradient(135deg, #ff6b6b, #ee5a5a)";
                }}
                onClick={confirmDeleteConcern}
              >
                🗑️ Delete Concern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Change Modal */}
      {showQuantityModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                borderRadius: "20px 20px 0 0",
                color: "white",
                padding: "25px 30px",
                borderBottom: "none",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
                {isQuantityRequest
                  ? "📝 Request Quantity Change"
                  : "📊 Change Delivery Quantity"}
              </h3>
              <button
                className="btn-close"
                onClick={() => setShowQuantityModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: "30px" }}>
              <div style={{ marginBottom: "20px" }}>
                <strong>Item:</strong>{" "}
                {selectedLogForQuantity?.dailyItem?.title}
                <br />
                <strong>Date:</strong>{" "}
                {selectedLogForQuantity &&
                  formatDate(selectedLogForQuantity.deliveryDate)}
                <br />
                <strong>Current Quantity:</strong>{" "}
                {selectedLogForQuantity?.quantityChanged
                  ? `${selectedLogForQuantity.newQuantity} (changed from ${selectedLogForQuantity.originalQuantity})`
                  : selectedLogForQuantity?.dailyItem?.scheduleQuantity ||
                    "Not specified"}
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  {isQuantityRequest ? "Requested Quantity:" : "New Quantity:"}
                </label>
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  min="1"
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    transition: "all 0.3s ease",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4CAF50";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(76, 175, 80, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  {isQuantityRequest
                    ? "Reason for Request:"
                    : "Reason for Change:"}{" "}
                  {!isQuantityRequest && "(Optional)"}
                </label>
                <textarea
                  value={quantityChangeReason}
                  onChange={(e) => setQuantityChangeReason(e.target.value)}
                  placeholder={
                    isQuantityRequest
                      ? "Please explain why you need this quantity change..."
                      : "Optional reason for the quantity change..."
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    transition: "all 0.3s ease",
                    outline: "none",
                    resize: "vertical",
                    minHeight: "80px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4CAF50";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(76, 175, 80, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {isQuantityRequest && (
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)",
                    border: "1px solid #ffc107",
                    borderRadius: "10px",
                    padding: "15px",
                    marginBottom: "20px",
                  }}
                >
                  <strong>ℹ️ Note:</strong> This will send a request to the
                  admin. They will review and approve/deny your quantity change
                  request.
                </div>
              )}
            </div>

            <div
              className="modal-footer"
              style={{ justifyContent: "center", gap: "15px" }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowQuantityModal(false)}
                style={{
                  padding: "12px 25px",
                  fontWeight: "500",
                  borderRadius: "10px",
                  border: "2px solid #6c757d",
                  background: "transparent",
                  color: "#6c757d",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#6c757d";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#6c757d";
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  background: "linear-gradient(135deg, #4CAF50, #45a049)",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px 25px",
                  fontSize: "16px",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 8px rgba(76, 175, 80, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 12px rgba(76, 175, 80, 0.4)";
                  e.target.style.background =
                    "linear-gradient(135deg, #45a049, #3d8b40)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 8px rgba(76, 175, 80, 0.3)";
                  e.target.style.background =
                    "linear-gradient(135deg, #4CAF50, #45a049)";
                }}
                onClick={handleQuantitySubmit}
                disabled={
                  !newQuantity.trim() ||
                  (isQuantityRequest && !quantityChangeReason.trim())
                }
              >
                {isQuantityRequest ? "📤 Send Request" : "✅ Update Quantity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Requests Modal - Separate from general concerns */}
      {showQuantityRequestsModal && selectedLogForConcerns && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: "700px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                borderRadius: "20px 20px 0 0",
                color: "white",
                padding: "25px 30px",
                borderBottom: "none",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
                📦 Quantity Change Requests
              </h3>
              <button
                className="btn-close"
                onClick={() => setShowQuantityRequestsModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: "30px" }}>
              {/* Delivery Info */}
              <div
                style={{
                  marginBottom: "25px",
                  padding: "20px",
                  backgroundColor: "#fff3e0",
                  borderRadius: "15px",
                  border: "2px solid #ffb74d",
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  <strong>📦 Item:</strong>{" "}
                  {selectedLogForConcerns.dailyItem?.title}
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <strong>📅 Date:</strong>{" "}
                  {formatDate(selectedLogForConcerns.deliveryDate)}
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <strong>👤 Assigned To:</strong>{" "}
                  {selectedLogForConcerns.assignedTo?.firstName}{" "}
                  {selectedLogForConcerns.assignedTo?.lastName} (ID:{" "}
                  {selectedLogForConcerns.assignedTo?.uniqueUserId})
                </div>
                <div>
                  <strong>📊 Current Quantity:</strong>{" "}
                  {selectedLogForConcerns.quantityChanged
                    ? `${selectedLogForConcerns.newQuantity} (changed from ${selectedLogForConcerns.originalQuantity})`
                    : selectedLogForConcerns.dailyItem?.scheduleQuantity ||
                      "Not specified"}
                </div>
              </div>

              {/* Quantity Requests List */}
              <div>
                <h4 style={{ marginBottom: "15px", color: "#ef6c00" }}>
                  📋 Quantity Change Requests
                </h4>

                {selectedLogForConcerns.concerns &&
                selectedLogForConcerns.concerns.filter((concern) =>
                  concern.message
                    .toLowerCase()
                    .includes("quantity change request")
                ).length > 0 ? (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {selectedLogForConcerns.concerns
                      .filter((concern) =>
                        concern.message
                          .toLowerCase()
                          .includes("quantity change request")
                      )
                      .sort(
                        (a, b) => new Date(b.raisedAt) - new Date(a.raisedAt)
                      )
                      .map((concern, idx) => {
                        // Extract requested quantity from message
                        const quantityMatch = concern.message.match(
                          /Requested quantity:\s*(\d+)/
                        );
                        const requestedQuantity = quantityMatch
                          ? quantityMatch[1]
                          : null;

                        return (
                          <div
                            key={idx}
                            style={{
                              marginBottom: "20px",
                              padding: "20px",
                              backgroundColor:
                                concern.status === "open"
                                  ? "#fff3e0"
                                  : "#e8f5e8",
                              borderRadius: "12px",
                              border: `2px solid ${
                                concern.status === "open"
                                  ? "#ff9800"
                                  : "#4caf50"
                              }`,
                            }}
                          >
                            {/* Request Header */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "15px",
                              }}
                            >
                              <div>
                                <strong style={{ color: "#e65100" }}>
                                  📤 Request from: {concern.raisedBy?.username}
                                </strong>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    marginTop: "4px",
                                  }}
                                >
                                  {formatDate(concern.raisedAt)} at{" "}
                                  {formatTime(concern.raisedAt)}
                                </div>
                              </div>
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "15px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  background:
                                    concern.status === "open"
                                      ? "#ff9800"
                                      : "#4caf50",
                                  color: "white",
                                }}
                              >
                                {concern.status.toUpperCase()}
                              </span>
                            </div>

                            {/* Request Details */}
                            <div
                              style={{
                                padding: "15px",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                marginBottom: "15px",
                              }}
                            >
                              <div style={{ marginBottom: "10px" }}>
                                <strong>📦 Requested Quantity:</strong>{" "}
                                <span
                                  style={{
                                    color: "#ff5722",
                                    fontWeight: "bold",
                                    fontSize: "18px",
                                  }}
                                >
                                  {requestedQuantity || "Not specified"}
                                </span>
                              </div>
                              <div style={{ color: "#555", lineHeight: "1.5" }}>
                                {concern.message}
                              </div>
                            </div>

                            {/* Admin Response */}
                            {concern.adminResponse && (
                              <div
                                style={{
                                  padding: "15px",
                                  backgroundColor: "#e8f5e8",
                                  borderRadius: "8px",
                                  borderLeft: "4px solid #4caf50",
                                  marginBottom: "15px",
                                }}
                              >
                                <strong style={{ color: "#2e7d32" }}>
                                  👨‍💼 Admin Response:
                                </strong>
                                <div
                                  style={{ marginTop: "8px", color: "#555" }}
                                >
                                  {concern.adminResponse}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    marginTop: "8px",
                                  }}
                                >
                                  Responded at:{" "}
                                  {formatDate(concern.respondedAt)} by{" "}
                                  {concern.respondedBy?.username}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons for Open Requests */}
                            {concern.status === "open" && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #4caf50, #45a049)",
                                    border: "none",
                                    borderRadius: "8px",
                                    padding: "10px 20px",
                                    color: "white",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    transition: "all 0.3s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform =
                                      "translateY(-2px)";
                                    e.target.style.boxShadow =
                                      "0 4px 8px rgba(76, 175, 80, 0.4)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = "translateY(0)";
                                    e.target.style.boxShadow = "none";
                                  }}
                                  onClick={() =>
                                    handleApproveQuantityRequest(
                                      concern,
                                      requestedQuantity
                                    )
                                  }
                                >
                                  ✅ Approve & Update Quantity
                                </button>

                                <button
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #f44336, #d32f2f)",
                                    border: "none",
                                    borderRadius: "8px",
                                    padding: "10px 20px",
                                    color: "white",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    transition: "all 0.3s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform =
                                      "translateY(-2px)";
                                    e.target.style.boxShadow =
                                      "0 4px 8px rgba(244, 67, 54, 0.4)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = "translateY(0)";
                                    e.target.style.boxShadow = "none";
                                  }}
                                  onClick={async () => {
                                    try {
                                      await dailyItemService.respondToConcern(
                                        selectedLogForConcerns._id,
                                        concern._id,
                                        "Quantity change request denied by admin",
                                        "dismissed"
                                      );
                                      toast.success(
                                        "Quantity change request denied"
                                      );
                                      // Close the modal and refresh data
                                      setShowQuantityRequestsModal(false);
                                      fetchDeliveryLogs();
                                    } catch (error) {
                                      toast.error("Failed to deny request");
                                    }
                                  }}
                                >
                                  ❌ Deny Request
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    📭 No quantity change requests for this delivery
                  </div>
                )}
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                justifyContent: "center",
                padding: "20px 30px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowQuantityRequestsModal(false)}
                style={{
                  padding: "12px 25px",
                  fontWeight: "500",
                  borderRadius: "10px",
                  border: "2px solid #6c757d",
                  background: "transparent",
                  color: "#6c757d",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#6c757d";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#6c757d";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryTracking;
